/**
 * Pure animation math + state-diff detection (no three.js / DOM — node-testable).
 *
 * The renderer (board.js) consumes:
 *  - EASE / lerp / hopY / wallDropY / glideDuration  — tween curves
 *  - classifyPawnAnim — picks hop / jump / wallJump / glide from the last Move
 *  - diffStates       — compares two core states and emits animation events
 *
 * None of this re-derives game rules: both states passed to diffStates were
 * already produced by the core, we only *describe the visual difference*.
 */

export const EASE = {
  linear: (t) => t,
  easeInQuad: (t) => t * t,
  easeOutQuad: (t) => t * (2 - t),
  easeOutCubic: (t) => 1 - (1 - t) ** 3,
  easeInOutQuad: (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2),
};

export function clamp01(t) {
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

export function lerp(a, b, t) {
  return a + (b - a) * t;
}

/** Parabolic hop: 0 at k=0 and k=1, apex = height at k=0.5. */
export function hopY(k, height) {
  return 4 * height * k * (1 - k);
}

/**
 * Wall-drop offset from the final resting y, over raw time t in [0,1]:
 * an ease-in fall from `startOffset` until t=FALL, then a small settle dip
 * (half sine, depth `sinkDepth`) that returns to exactly 0 at t=1.
 */
export function wallDropY(t, startOffset, sinkDepth = 0.05) {
  const FALL = 0.65;
  if (t <= 0) return startOffset;
  if (t >= 1) return 0;
  if (t < FALL) return startOffset * (1 - EASE.easeInQuad(t / FALL));
  return -sinkDepth * Math.sin(Math.PI * ((t - FALL) / (1 - FALL)));
}

/** Ice-slide duration grows with distance, clamped to [200, 600] ms. */
export function glideDuration(distCells) {
  return Math.max(200, Math.min(600, 140 + 70 * distCells));
}

const cellKey = (c, r) => c + ',' + r;

/** Is any cell strictly between from and to (straight line) an ice tile? */
export function hasIceBetween(from, to, gimmicks) {
  if (!gimmicks || gimmicks.length === 0) return false;
  const dc = Math.sign(to.col - from.col);
  const dr = Math.sign(to.row - from.row);
  if ((dc !== 0) === (dr !== 0)) return false; // not a straight move
  const ice = new Set(
    gimmicks.filter((g) => g.type === 'ice').map((g) => cellKey(g.col, g.row)),
  );
  if (ice.size === 0) return false;
  let c = from.col + dc;
  let r = from.row + dr;
  while (c !== to.col || r !== to.row) {
    if (ice.has(cellKey(c, r))) return true;
    c += dc;
    r += dr;
  }
  return false;
}

/**
 * Pick the visual for a pawn displacement from the applied Move.
 * Returns { kind, duration(ms), height, spin(rad), ease(name in EASE) }.
 *
 * - wallJump / diagonalStep → high arc (wallJump adds a full spin)
 * - straight 2+ cells across ice → low fast glide (distance-scaled duration)
 * - straight 2 cells / 1-1 diagonal → high arc (face-to-face jump / dodge)
 * - anything longer or L-shaped (jump-then-slide etc.) → glide
 * - plain 1-step → small hop
 */
export function classifyPawnAnim(move, from, to, gimmicks) {
  const adc = Math.abs(to.col - from.col);
  const adr = Math.abs(to.row - from.row);
  const dist = adc + adr;
  const straight = (adc === 0) !== (adr === 0);
  const glide = () => ({
    kind: 'glide',
    duration: glideDuration(dist),
    height: 0.05,
    spin: 0,
    ease: 'easeOutCubic',
  });

  if (move && move.type === 'ability' && move.ability === 'wallJump') {
    return {
      kind: 'wallJump',
      duration: 340 + 30 * Math.max(0, dist - 1),
      height: 1.0,
      spin: Math.PI * 2,
      ease: 'easeOutQuad',
    };
  }
  if (move && move.type === 'ability' && move.ability === 'diagonalStep') {
    return { kind: 'jump', duration: 340, height: 0.85, spin: 0, ease: 'easeOutQuad' };
  }
  if (straight && dist >= 2 && hasIceBetween(from, to, gimmicks)) return glide();
  if (dist === 1) {
    return { kind: 'hop', duration: 240, height: 0.45, spin: 0, ease: 'easeOutQuad' };
  }
  if (adc === 1 && adr === 1) {
    return { kind: 'jump', duration: 340, height: 0.85, spin: 0, ease: 'easeOutQuad' };
  }
  if (straight && dist === 2) {
    return { kind: 'jump', duration: 340, height: 0.9, spin: 0, ease: 'easeOutQuad' };
  }
  return glide();
}

/** Stable identity for a wall (owner excluded — a slot can hold one wall). */
export function wallKey(w) {
  return `${w.col},${w.row},${w.orientation}`;
}

/**
 * Diff two core states into animation events.
 * Returns {
 *   snap: boolean,            // true → place everything instantly, no anims
 *   pawns: [{ player, from, to, anim }],
 *   wallsAdded: wall[],       // walls present in next but not prev
 *   wallsRemoved: wall[],     // walls present in prev but not next (mage)
 *   victory: null | 0 | 1,    // winner newly decided by this diff
 * }
 * A shorter history means a new game — snap. Same object means a pure
 * highlight refresh — no events.
 */
export function diffStates(prev, next) {
  const none = { snap: false, pawns: [], wallsAdded: [], wallsRemoved: [], victory: null };
  if (!prev) return { ...none, snap: true };
  if (next === prev) return none;
  if (next.history.length < prev.history.length) return { ...none, snap: true };

  const lastMove =
    next.history.length > prev.history.length
      ? next.history[next.history.length - 1]
      : null;

  const pawns = [];
  for (let i = 0; i < next.players.length; i++) {
    const a = prev.players[i].pawn;
    const b = next.players[i].pawn;
    if (a.col !== b.col || a.row !== b.row) {
      pawns.push({
        player: i,
        from: { col: a.col, row: a.row },
        to: { col: b.col, row: b.row },
        anim: classifyPawnAnim(lastMove, a, b, next.config.gimmicks),
      });
    }
  }

  const prevW = new Map(prev.walls.map((w) => [wallKey(w), w]));
  const nextW = new Map(next.walls.map((w) => [wallKey(w), w]));
  const wallsAdded = [...nextW].filter(([k]) => !prevW.has(k)).map(([, w]) => w);
  const wallsRemoved = [...prevW].filter(([k]) => !nextW.has(k)).map(([, w]) => w);

  const victory = next.winner !== null && prev.winner === null ? next.winner : null;
  return { snap: false, pawns, wallsAdded, wallsRemoved, victory };
}
